import { policyList } from "@/enums/permissions";
import { HttpQuery } from "@/interfaces/httpQuery";
import { Location, locationDefault } from "@/interfaces/location";
import {
  PmpConfig,
  pmpConfigDefault,
  PmpHistoryLogs,
  RetailSettings
} from "@/interfaces/pmp";
import {
  PmpIntegrationHistoryLogs,
  pmpValidationDictionary
} from "@/metadata/pmp";
import { locationsService } from "@/services/locations.service";
import { messagesService } from "@/services/messages.service";
import { pmpService } from "@/services/pmp.service";
import {
  BooleanCheck,
  PageTitleComponent,
  TableComponent,
  TableHeader,
  TablePagination,
  TablePaginationDefault
} from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import { Validator } from "vee-validate";
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import NewConfirmModalComponent from "../PharmacyModeConfirmModal/PharmacyModeConfirm.component";
import PmpIntegrationConnectionModalComponent from "../PmpIntegrationConnectionModal/PmpIntegrationConnectionModal.component";
import Template from "./PmpIntegrationEdit.template.vue";

@Component({
  mixins: [Template],
  components: {
    TableComponent,
    PageTitleComponent
  }
})
export default class PmpIntegrationEditComponent extends Vue {
  @Getter("hasPermission", { namespace: "authModule" })
  public hasPermission!: BooleanCheck;
  public get submissionInterval(): Array<{ value: string; text: string }> {
    return [
      {
        value: "5 MIN",
        text: this.$t("pmp.submissionIntervalItems.5_min").toString()
      },
      {
        value: "24 HRS",
        text: this.$t("pmp.submissionIntervalItems.24_hrs").toString()
      }
    ];
  }
  public get encryptionItems(): Array<{ value: string; text: string }> {
    return [
      { value: "SFTP", text: this.$t("pmp.encryptionItems.sftp").toString() }
    ];
  }
  public get enabledLabel(): string {
    return this.pmpConfig.status ? "Enabled" : "Disabled";
  }
  @Inject() public $validator!: Validator;
  public loading = false;
  public location: Location = locationDefault;
  public hasConfig: boolean = false;
  public isLoading = false;
  public pmpHistoryLogs: PmpHistoryLogs[] = [];
  public pmpConfig: PmpConfig = cloneDeep(pmpConfigDefault);
  public currentPmpConfig: PmpConfig = cloneDeep(pmpConfigDefault);
  public pagination: TablePagination = { ...TablePaginationDefault };
  public tableHeaders: TableHeader[] = PmpIntegrationHistoryLogs;
  public pharmacyMode: RetailSettings = { id: null, status: null };
  public connectionCheck: string = "";
  public async changePagination(pagination: TablePagination) {
    this.pagination = pagination;
    this.isLoading = true;
    this.isLoading = false;
  }
  public async checkPharmacyStatus() {
    const locationId = this.$route.params.id;
    this.pharmacyMode = await pmpService.getPharmacyMode(+locationId);
  }

  public async checkpmpIntegration() {
    const locationsId = this.$route.params.id;
    this.pharmacyMode = await pmpService.getPharmacyMode(+locationsId);
    if (this.pmpConfig.status) {
      if (!this.pharmacyMode.status) {
        const confirm = (await this.$modals
          .load(
            NewConfirmModalComponent,
            { size: "normal", positionX: "center", positionY: "center" },
            {
              ftpModalData: {
                title: "pmp.ftpConfigTitle",
                body: "pmp.ftpConfigBody",
                titleClass: "pmp_model",
                accept: "pmp.ftpaccept",
                cancel: "pmp.ftpcancel"
              }
            }
          )
          .catch(() => false)) as boolean;
        if (confirm) {
          const locationId = this.pharmacyMode.id;
          const pharmacyModeStatus = {
            pharmacy_mode_enabled: true
          };
          const pharmacyModeUpdate = await pmpService.enablePMPIntegration(
            locationId,
            pharmacyModeStatus
          );
          if (pharmacyModeUpdate === "success") {
            this.pmpConfig.status = 1;
          } else {
            this.pmpConfig.status = 0;
          }
        } else {
          this.pmpConfig.status = 0;
        }
      }
    }
  }

  public cancel() {
    this.$router.back();
    this.pmpConfig = cloneDeep(pmpConfigDefault);
  }

  public async save() {
    const pmpEditValidation = await this.$validator.validateAll();
    if (!this.pmpConfig.status && pmpEditValidation) {
      this.saveDetails(pmpEditValidation);
    } else if (this.hasConfig) {
      this.connectionCheck = await pmpService.updatePmpFtpConfig(
        this.pmpConfig,
        this.pmpConfig.id!
      );
    } else {
      this.connectionCheck = await pmpService.setPmpFtpConfig(
        this.pmpConfig,
        this.location
      );
    }
    if (
      this.pmpConfig.status &&
      pmpEditValidation &&
      this.connectionCheck !== "Error"
    ) {
      const confirm = (await this.$modals
        .load(
          PmpIntegrationConnectionModalComponent,
          { size: "normal", positionX: "center", positionY: "center" },
          {
            connectionModalData: {
              title: "pmp.connectionCheck.successMessageTitle",
              body: "pmp.connectionCheck.successMessage",
              accept: "pmp.connectionCheck.connectionaccept",
              titleClass: "success_title"
            }
          }
        )
        .catch(() => false)) as boolean;
      if (confirm) {
        this.$router.push({
          name: "pmp-integration"
        });
      }
    } else if (
      this.pmpConfig.status &&
      pmpEditValidation &&
      this.connectionCheck === "Error"
    ) {
      const confirm = await this.$modals.load(
        PmpIntegrationConnectionModalComponent,
        { size: "normal", positionX: "center", positionY: "center" },
        {
          connectionModalData: {
            title: "pmp.connectionCheck.errorMessageTitle",
            body: "pmp.connectionCheck.errorMessage",
            accept: "pmp.connectionCheck.connectionaccept",
            titleClass: "error_title"
          }
        }
      );
      this.pmpConfig.status = 0;
    }
  }

  public async saveDetails(pmpEditValidation: boolean) {
    if (pmpEditValidation) {
      if (this.currentPmpConfig.status && !this.pmpConfig.status) {
        const confirm = (await this.$modals
          .load(
            NewConfirmModalComponent,
            { size: "normal", positionX: "center", positionY: "center" },
            {
              ftpModalData: {
                title: "pmp.warnDisableIntegrationTitle",
                body: "pmp.warnDisableIntegrationBody",
                titleClass: "warn_disable_title",
                accept: "accept",
                cancel: "cancel"
              }
            }
          )
          .catch(() => false)) as boolean;
        if (confirm) {
          if (this.hasConfig) {
            this.loading = true;
            await pmpService.updatePmpFtpConfig(
              this.pmpConfig,
              this.pmpConfig.id!
            );
          } else {
            this.loading = true;
            await pmpService.setPmpFtpConfig(this.pmpConfig, this.location);
          }
          this.pmpConfig = cloneDeep(pmpConfigDefault);

          this.$router.push({
            name: "pmp-integration"
          });
        }
      } else {
        if (this.hasConfig) {
          this.loading = true;
          await pmpService.updatePmpFtpConfig(
            this.pmpConfig,
            this.pmpConfig.id!
          );
        } else {
          this.loading = true;
          await pmpService.setPmpFtpConfig(this.pmpConfig, this.location);
        }
        this.pmpConfig = cloneDeep(pmpConfigDefault);

        this.$router.push({
          name: "pmp-integration"
        });
      }
    }
  }

  public onChangePage(pagination: TablePagination) {
    this.buildTable(pagination);
  }

  public async getHistoryLogs(query?: HttpQuery) {
    const logs = await pmpService.getHistoryPmpFtpConfig(query);
    this.pagination = await pmpService.getPagination();
    return logs;
  }

  public disableSaveButton() {
    return !this.hasPermission(policyList.modifyPmpIntegration);
  }

  public async mounted() {
    this.pmpConfig = cloneDeep(pmpConfigDefault);
    this.loading = true;
    this.isLoading = true;
    await this.checkPharmacyStatus();
    const locationId = this.$route.params.id;
    this.location = await locationsService.findById(+locationId, {
      embed: "state"
    });
    const config = await pmpService.getCurrentPmpFtpConfig([+locationId]);
    if (config.length) {
      this.hasConfig = true;
      this.currentPmpConfig = cloneDeep(config[0]);
      this.pmpConfig = config[0];
    }
    this.loading = false;
    this.buildTable(this.pagination);

    this.$validator.localize("en", pmpValidationDictionary);
    this.isLoading = false;
  }

  protected async buildTable(pagination: TablePagination) {
    this.isLoading = true;
    const response = await this.getHistoryLogs({
      pmp_ftp_config_id: this.pmpConfig.id,
      per_page: pagination.itemsPerPage,
      page: pagination.currentPage
    });
    this.pmpHistoryLogs = response;
    this.isLoading = false;
  }
}
